import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { AppLayout } from "@/components/AppLayout";
import { Users, CalendarClock, UserX, ClipboardList, Inbox, FileCheck, CalendarDays } from "lucide-react";

function formatDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" });
}

const COUNTERS = [
  { key: "membres_total", label: "Membres (total)", icon: Users, color: "text-cfhtb-heading", testid: "counter-total-members" },
  { key: "membres_actifs", label: "Membres actifs", icon: Users, color: "text-cfhtb-primary", testid: "counter-active-members" },
  { key: "adhesions_expirant_30j", label: "Adhésions expirant < 30 j", icon: CalendarClock, color: "text-cfhtb-warning", testid: "counter-expiring" },
  { key: "fiches_en_attente", label: "Fiches en attente", icon: FileCheck, color: "text-cfhtb-secondary", testid: "counter-pending-profiles" },
  { key: "inscriptions_seminaires", label: "Inscriptions événements", icon: ClipboardList, color: "text-cfhtb-success", testid: "counter-enrollments" },
];

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [seminaires, setSeminaires] = useState(null);

  useEffect(() => {
    api("/admin/stats").then(setStats).catch(() => {});
    api("/admin/seminaires").then(setSeminaires).catch(() => {});
  }, []);

  return (
    <AppLayout variant="admin">
      <div data-testid="admin-dashboard" className="space-y-8 animate-fade-in">
        <h1 className="font-serif text-3xl font-bold text-cfhtb-heading tracking-tight">Tableau de bord</h1>

        {!stats ? (
          <div className="h-8 w-8 border-2 border-cfhtb-primary border-t-transparent rounded-full animate-spin" />
        ) : (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
              {COUNTERS.map(({ key, label, icon: Icon, color, testid }) => (
                <div key={key} data-testid={testid} className="bg-white rounded-lg border border-cfhtb-light/30 shadow-sm p-5 hover:shadow-md transition-shadow">
                  <div className="flex items-center justify-between">
                    <p className="text-xs uppercase tracking-wider font-medium text-cfhtb-text/80">{label}</p>
                    <Icon className={`h-5 w-5 ${color}`} />
                  </div>
                  <p className="font-serif text-3xl font-bold text-cfhtb-heading mt-2">{stats[key]}</p>
                </div>
              ))}
            </div>

            <section className="bg-white rounded-lg border border-cfhtb-light/30 shadow-sm p-6" data-testid="events-enrollment-section">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-serif text-xl font-semibold text-cfhtb-heading flex items-center gap-2">
                  <CalendarDays className="h-5 w-5 text-cfhtb-primary" /> Inscrits par événement
                </h2>
                <Link to="/admin/seminaires" className="text-xs font-medium text-cfhtb-secondary hover:text-cfhtb-primary" data-testid="view-all-events-link">
                  Tout voir
                </Link>
              </div>
              {!seminaires ? (
                <p className="text-sm text-cfhtb-text">Chargement…</p>
              ) : seminaires.length === 0 ? (
                <p className="text-sm text-cfhtb-text">Aucun événement.</p>
              ) : (
                <ul className="divide-y divide-slate-100" data-testid="events-enrollment-list">
                  {seminaires.map((s) => (
                    <li key={s.id} data-testid={`event-count-${s.id}`}>
                      <Link to={`/admin/seminaires/${s.id}/inscrits`} className="py-3 flex items-center justify-between gap-3 hover:bg-cfhtb-bg/40 -mx-2 px-2 rounded transition-colors">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-cfhtb-heading truncate">{s.titre}</p>
                          <p className="text-xs text-cfhtb-text">{formatDate(s.date_debut)}</p>
                        </div>
                        <span className="shrink-0 inline-flex items-center gap-1.5 bg-cfhtb-primary/10 text-cfhtb-primary rounded-full px-3 py-1 text-sm font-semibold">
                          <Users className="h-3.5 w-3.5" /> {s.nb_inscrits}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <section className="bg-white rounded-lg border border-cfhtb-light/30 shadow-sm p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-serif text-xl font-semibold text-cfhtb-heading">Dernières inscriptions</h2>
                  <Link to="/admin/membres" className="text-xs font-medium text-cfhtb-secondary hover:text-cfhtb-primary" data-testid="view-all-members-link">
                    Tout voir
                  </Link>
                </div>
                {stats.derniers_membres.length === 0 ? (
                  <p className="text-sm text-cfhtb-text">Aucun membre.</p>
                ) : (
                  <ul className="divide-y divide-slate-100" data-testid="recent-members-list">
                    {stats.derniers_membres.map((m) => (
                      <li key={m.id}>
                        <Link to={`/admin/membres/${m.id}`} className="py-3 flex items-center justify-between gap-3 hover:bg-cfhtb-bg/40 -mx-2 px-2 rounded transition-colors">
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-cfhtb-heading truncate">
                              {m.prenom} {m.nom || <span className="text-cfhtb-text/60 italic">(sans nom)</span>}
                            </p>
                            <p className="text-xs text-cfhtb-text truncate">{m.email_principal}</p>
                          </div>
                          <span className="text-xs text-cfhtb-text shrink-0">{formatDate(m.created_at)}</span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </section>

              <section className="bg-white rounded-lg border border-cfhtb-light/30 shadow-sm p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-serif text-xl font-semibold text-cfhtb-heading">Activité récente</h2>
                  <Link to="/admin/journal" className="text-xs font-medium text-cfhtb-secondary hover:text-cfhtb-primary" data-testid="view-audit-link">
                    Journal complet
                  </Link>
                </div>
                {stats.audit_recent.length === 0 ? (
                  <p className="text-sm text-cfhtb-text">Aucune activité enregistrée.</p>
                ) : (
                  <ul className="divide-y divide-slate-100" data-testid="recent-audit-list">
                    {stats.audit_recent.map((a) => (
                      <li key={a.id} className="py-3 flex items-center gap-3">
                        <Inbox className="h-4 w-4 text-cfhtb-light shrink-0" />
                        <div className="min-w-0">
                          <p className="text-sm text-cfhtb-heading truncate">
                            <span className="font-medium">{a.action}</span>
                            <span className="text-cfhtb-text"> · {a.entite}</span>
                          </p>
                          <p className="text-xs text-cfhtb-text">{formatDate(a.date)}</p>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </div>
          </>
        )}
      </div>
    </AppLayout>
  );
}
